import * as React from 'react';
import styled from 'styled-components';
import { debounce } from '@agilepoint/utils-common';
import { IToolbarItemProps, IToolbarRendererProps } from '../types';
import { ToolbarItem } from '../toolbar-item';
import { useWindowSizeContext } from '../../../../utils';
import { NavigationButton } from './navigation-button';
import { IStackPanelProps, StackPanel } from '../../../stack-panel';

const ItemsWrapper = styled(StackPanel)`
    height: 100%;
    width: auto;
    display: flex;
    overflow: hidden;
    flex-shrink: 1;
    background-color: ${({ theme: { toolbar } }) => toolbar.colors.backgroundColor};
`;

const Wrapper = styled.div`
    display: grid;
    width: 100%;
    height: 100%;
    align-items: center;
    grid-template-columns: auto auto;
    background-color: ${({ theme: { toolbar } }) => toolbar.colors.backgroundColor};
`;

function useOverflowDetector(itemWrapperRef: React.RefObject<HTMLDivElement>, commands: IToolbarItemProps[]) {
    const [isItemsOverflowing, setOverflowState] = React.useState<boolean>(false);
    const { windowWidth } = useWindowSizeContext();

    const checkOverflow = React.useCallback(() => {
        const { current } = itemWrapperRef;
        if (!current) {
            return;
        }
        setOverflowState(current.offsetWidth !== current.scrollWidth);
    }, [itemWrapperRef]);

    React.useEffect(checkOverflow, [checkOverflow, windowWidth, commands]);

    return { isItemsOverflowing, recheckOverflow: checkOverflow };
}

type ScrollDirection = 'left' | 'right';

function useScrollNavigator(itemWrapperRef: React.RefObject<HTMLDivElement>, scrollDisplacement = 150) {
    const [canScrollLeft, setScrollLeftState] = React.useState<boolean>(false);
    const [canScrollRight, setScrollRightState] = React.useState<boolean>(true);
    const { windowWidth } = useWindowSizeContext();

    const updateScrollStatus = React.useCallback((newScrollPosition?: number) => {
        const { current } = itemWrapperRef;
        if (!current) {
            return;
        }
        const { scrollWidth, clientWidth } = current;
        const scrollLeft = newScrollPosition ?? current.scrollLeft;
        setScrollLeftState(scrollLeft > 0);
        setScrollRightState(scrollLeft < (scrollWidth - clientWidth));
    }, [itemWrapperRef]);

    React.useEffect(updateScrollStatus, [updateScrollStatus, windowWidth]);

    const doScroll = React.useCallback((direction: ScrollDirection) => {
        const { current } = itemWrapperRef;
        if (!current) {
            return;
        }
        const { scrollLeft } = current;
        const newScrollPosition = Math.max(0, scrollLeft + (scrollDisplacement * (direction === 'left' ? -1 : 1)));
        current.scrollTo({
            left: newScrollPosition,
            behavior: 'smooth'
        });
        updateScrollStatus(newScrollPosition);
    }, [itemWrapperRef, scrollDisplacement, updateScrollStatus]);

    const onScrollLeft = React.useCallback(() => doScroll('left'), [doScroll]);
    const onScrollRight = React.useCallback(() => doScroll('right'), [doScroll]);
    return { canScrollLeft, canScrollRight, onScrollLeft, onScrollRight, recheckScrollState: updateScrollStatus };
}

interface IToolbarOverflowCarouselItemProps extends Omit<IToolbarRendererProps, 'farPositionedToolbarItems' | 'primaryToolbarItems' | 'containerRef'>, Pick<IStackPanelProps, 'justifyContent'> {
    commands: IToolbarItemProps[];
}

const ToolbarOverflowCarouselItem = React.memo((props: IToolbarOverflowCarouselItemProps) => {
    const { commands, iconSize, iconPosition, separator, separatorGap, height, justifyContent } = props;
    const itemWrapperRef = React.useRef<HTMLDivElement>(null);
    const { isItemsOverflowing, recheckOverflow } = useOverflowDetector(itemWrapperRef, commands);
    const { canScrollLeft, canScrollRight, onScrollLeft, onScrollRight, recheckScrollState } = useScrollNavigator(itemWrapperRef);

    const recheck = React.useMemo(() => debounce(() => {
        recheckOverflow();
        recheckScrollState();
    }, 100, null), [recheckOverflow, recheckScrollState]);

    const _onRenderCommand = React.useCallback((itemProps: IToolbarItemProps, index: number) => (
        <ToolbarItem iconPosition={iconPosition} iconSize={iconSize} {...itemProps} onItemLoad={recheck} key={itemProps.id} itemIndex={index} height={height} />
    ), [iconSize, iconPosition, height, recheck]);

    return (
        <ItemsWrapper alignment="center" justifyContent={justifyContent}>
            <NavigationButton className="ap-toolbar-leftNavigationButton" iconName="ap-left-arrow-regular.outline" hidden={!isItemsOverflowing} disabled={!canScrollLeft} onClick={onScrollLeft} />
            <ItemsWrapper gap={separatorGap} separator={separator} alignment="center" elementRef={itemWrapperRef}>
                {commands.map(_onRenderCommand)}
            </ItemsWrapper>
            <NavigationButton className="ap-toolbar-rightNavigationButton" iconName="ap-right-arrow-regular.outline" hidden={!isItemsOverflowing} disabled={!canScrollRight} onClick={onScrollRight} />
        </ItemsWrapper>
    );
});

ToolbarOverflowCarouselItem.displayName = 'ToolbarOverflowCarouselItem';

export const ToolbarOverflowCarousel = React.memo((props: IToolbarRendererProps) => {
    const { containerRef, primaryToolbarItems, farPositionedToolbarItems, ...rest } = props;
    return (
        <Wrapper ref={containerRef}>
            <ToolbarOverflowCarouselItem commands={primaryToolbarItems} {...rest} />
            {farPositionedToolbarItems.length ? (
                <ToolbarOverflowCarouselItem commands={farPositionedToolbarItems} justifyContent="flex-end" {...rest} />
            ) : null}
        </Wrapper>
    );
});

ToolbarOverflowCarousel.displayName = 'ToolbarOverflowCarousel';
